import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, useToast } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type {
  CarrierResolution,
  ColumnMapping,
  ImportBatch,
  ImportField,
  ParseResult,
  PayerDirectoryEntry,
  PreviewResult,
} from '../api/verificationApi';
import {
  useDeleteImportBatch,
  useImportBatches,
  useImportFields,
  useImportFlow,
} from '../hooks/useImportFlow';
import { useCanDelete } from '../hooks/useCanDelete';
import { IV_BASE, IvSubnav } from '../components/IvSubnav';
import { ImportStepper } from '../components/ImportStepper';
import { UploadStep } from '../components/UploadStep';
import { MapColumnsStep } from '../components/MapColumnsStep';
import { ResolveCarriersStep } from '../components/ResolveCarriersStep';
import { PreviewStep } from '../components/PreviewStep';
import { getErrorMessage } from '../utils/format';
import styles from './ImportPage.module.css';

export function ImportPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const flow = useImportFlow();
  const { fields } = useImportFields();
  const { batches, isLoading: batchesLoading } = useImportBatches();
  const canDelete = useCanDelete();
  const deleteBatch = useDeleteImportBatch();

  function handleDeleteBatch(batch: ImportBatch) {
    deleteBatch.mutate(batch.id, {
      onSuccess: (r) =>
        toast.success(
          `Import deleted · ${r.deletedVerifications} appointment${r.deletedVerifications === 1 ? '' : 's'} removed` +
            (r.keptWrittenToPms > 0
              ? ` · ${r.keptWrittenToPms} kept (already written to Open Dental)`
              : ''),
        ),
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't delete this import")),
    });
  }

  const [step, setStep] = useState(1);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [mappingError, setMappingError] = useState<string | null>(null);
  const [carriers, setCarriers] = useState<CarrierResolution[]>([]);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  // ── Step 1 ────────────────────────────────────────────────────────────────

  function onParsed(result: ParseResult) {
    setParsed(result);
    setMapping(result.mapping ?? {});
    setMappingError(null);
    setStep(2);
  }

  function handleFile(file: File) {
    flow.parseFile.mutate(file, {
      onSuccess: onParsed,
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't read that file")),
    });
  }

  function handleSample() {
    flow.parseSample.mutate(undefined, {
      onSuccess: onParsed,
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't load the sample file")),
    });
  }

  function handleDownloadTemplate() {
    flow.downloadTemplate.mutate(undefined, {
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't download the template")),
    });
  }

  // ── Step 2 ────────────────────────────────────────────────────────────────

  function handleMappingChange(field: ImportField, header: string | null) {
    setMapping((m) => ({ ...m, [field]: header }));
    setMappingError(null);
  }

  async function handleMappingContinue() {
    if (!parsed) return;
    setMappingError(null);
    try {
      await flow.saveMapping.mutateAsync(mapping);
      const resolved = await flow.resolveCarriers.mutateAsync({
        importId: parsed.importId,
        mapping,
      });
      setCarriers(resolved);
      setStep(3);
    } catch (err) {
      setMappingError(getErrorMessage(err, "Couldn't save the column mapping"));
    }
  }

  // ── Step 3 ────────────────────────────────────────────────────────────────

  function handleSelectPayer(carrierNameRaw: string, payer: PayerDirectoryEntry | null) {
    setCarriers((list) =>
      list.map((c) =>
        c.carrierNameRaw !== carrierNameRaw
          ? c
          : {
              ...c,
              payerId: payer?.payerId ?? null,
              payerName: payer?.name ?? null,
              matchSource: payer ? 'suggested' : 'none',
            },
      ),
    );
  }

  async function handleCarriersContinue() {
    if (!parsed) return;
    try {
      const matched = carriers.filter(
        (c): c is CarrierResolution & { payerId: string; payerName: string } =>
          !!c.payerId && !!c.payerName,
      );
      if (matched.length > 0) {
        await flow.confirmCarriers.mutateAsync(
          matched.map((c) => ({
            carrierNameRaw: c.carrierNameRaw,
            payerId: c.payerId,
            payerName: c.payerName,
          })),
        );
      }
      const result = await flow.preview.mutateAsync({ importId: parsed.importId, mapping });
      setPreview(result);
      setStep(4);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't build the preview"));
    }
  }

  // ── Step 4 ────────────────────────────────────────────────────────────────

  function handleImport() {
    if (!parsed) return;
    flow.commit.mutate(
      { importId: parsed.importId, mapping },
      {
        onSuccess: (r) => {
          toast.success(
            `${r.created} imported, ${r.updated} updated · ${r.checksQueued} electronic checks started · ${r.failed} flagged` +
              (r.skipped > 0 ? ` · ${r.skipped} skipped (no name or duplicate)` : ''),
          );
          navigate(IV_BASE);
        },
        onError: (err) => toast.error(getErrorMessage(err, 'Import failed')),
      },
    );
  }

  return (
    <div className={patterns.page}>
      <IvSubnav />
      <PageHeader
        title="Import schedule"
        subtitle="For practices not yet connected to a practice-software sync, upload a day or week of appointments. Each row becomes a verification and runs an electronic check automatically."
      />

      <div className={styles.stepper}>
        <ImportStepper current={step} />
      </div>

      {step === 1 && (
        <UploadStep
          onFile={handleFile}
          onUseSample={handleSample}
          onDownloadTemplate={handleDownloadTemplate}
          parsing={flow.parseFile.isPending || flow.parseSample.isPending}
          downloading={flow.downloadTemplate.isPending}
          batches={batches}
          batchesLoading={batchesLoading}
          onDeleteBatch={canDelete ? handleDeleteBatch : undefined}
          deletingBatchId={deleteBatch.isPending ? deleteBatch.variables : null}
        />
      )}

      {step === 2 && parsed && (
        <MapColumnsStep
          fileName={parsed.fileName}
          rowCount={parsed.rowCount}
          headers={parsed.headers}
          fields={fields}
          mapping={mapping}
          onChange={handleMappingChange}
          onBack={() => setStep(1)}
          onContinue={handleMappingContinue}
          saving={flow.saveMapping.isPending || flow.resolveCarriers.isPending}
          error={mappingError}
        />
      )}

      {step === 3 && parsed && (
        <ResolveCarriersStep
          carriers={carriers}
          onSelect={handleSelectPayer}
          onBack={() => setStep(2)}
          onContinue={handleCarriersContinue}
          saving={flow.confirmCarriers.isPending || flow.preview.isPending}
        />
      )}

      {step === 4 && preview && (
        <PreviewStep
          preview={preview}
          onBack={() => setStep(3)}
          onImport={handleImport}
          importing={flow.commit.isPending}
        />
      )}
    </div>
  );
}
